import React, { useRef, useCallback } from "react";
import { Skeleton, Box, CircularProgress, Stack } from "@mui/material";

import ChatCard from "@/views/Patient/Chat/ChatCard";
import { MESSAGE_TYPE } from "@/models/chat";
import { ISessionKeys } from "@/models";
import { IRoleType } from "@/models/role";
import { getLatestUnreadOrRecentLastMessage, getMessageType } from "@/utils";
import { getLastMessageTime } from "@/utils";
import { storage } from "@/utils/Storage";
import { useAppSelector } from "@/store/hooks";
import { chat } from "@/store/slices/chat";

interface IProps {
  onSelectChat: (item: any) => void;
  chatList: any[];
  onLoadMore?: () => void;
  isLoading?: boolean;
}

const ChatList: React.FC<IProps> = (props) => {
  const { onSelectChat, chatList, onLoadMore, isLoading = false } = props;

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);
  const currentUserId = storage.getSessionToken(ISessionKeys.USER_ID);
  const { selectedChat, isChatSearchOn } = useAppSelector(chat);

  const observer = useRef<IntersectionObserver>();
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && onLoadMore) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, onLoadMore]
  );

  const renderChatCard = (item: any, index: number) => {
    const isPatient = roleType === IRoleType.PATIENT;
    const isStaffInvolved =
      item?.users?.staff_members?.includes(currentUserId) &&
      item?.users?.staff_members?.includes(
        item?.lastMessage?.patient?.sender_id
      );
    const lastMsgPatient = isStaffInvolved ? null : item?.lastMessage?.patient;
    const lastMsgInternal = item?.lastMessage?.internal;
    const lastPatientSeen = item?.lastMessage?.patient?.seen;
    let unreadMessageCount = 0;

    if (isPatient) {
      unreadMessageCount =
        (lastMsgPatient?.seen &&
          lastMsgPatient?.seen[currentUserId]?.unReadMessageCount) ||
        0;
    } else if (
      lastMsgInternal?.seen &&
      lastMsgInternal?.seen[currentUserId]?.unReadMessageCount > 0 &&
      lastMsgInternal?.sender_id !== currentUserId
    ) {
      unreadMessageCount =
        lastMsgInternal?.seen[currentUserId]?.unReadMessageCount || 0;
    } else if (
      lastPatientSeen &&
      lastPatientSeen[currentUserId]?.unReadMessageCount > 0 &&
      !isStaffInvolved
    ) {
      unreadMessageCount =
        (lastPatientSeen &&
          lastPatientSeen[currentUserId]?.unReadMessageCount) ||
        0;
    }
    const messageData = isPatient
      ? lastMsgPatient
      : getLatestUnreadOrRecentLastMessage(item?.lastMessage);
    const lastMessage =
      messageData?.type === MESSAGE_TYPE.TEXT
        ? messageData?.message
        : getMessageType[messageData?.type] ?? "";

    const lastMessageTime = messageData
      ? getLastMessageTime(messageData?.timestamp)
      : item?.createdAt
      ? getLastMessageTime(item?.createdAt, true)
      : null;

    const userName = item?.member_details?.name ?? item?.user_details?.name ?? (
      <Skeleton sx={{ width: "8rem" }} />
    );
    const mentionCount =
      lastPatientSeen && lastPatientSeen[currentUserId]?.mentions?.length;
    const mentions =
      lastPatientSeen && lastPatientSeen[currentUserId]?.mentions;

    return (
      <ChatCard
        key={`${index}-${item?.id}`}
        mentionCount={mentionCount}
        isSelected={selectedChat?.id === item?.id}
        unreadMessageCount={unreadMessageCount}
        userName={userName}
        profilePicture={item?.user_details?.profileImage}
        lastMessage={lastMessage}
        lastMessageTime={lastMessageTime}
        type={item?.type}
        roomName={
          isChatSearchOn ? item?.roomName : item?.inpatient_details?.room_name
        }
        bedNumber={
          isChatSearchOn ? item?.bedNumber : item?.inpatient_details?.bed_number
        }
        isFavorite={item?.isFavorite}
        mentions={mentions}
        onSelect={() => onSelectChat(item)}
      />
    );
  };

  if (onLoadMore) {
    return (
      <Stack
        height={"calc(100% - 5rem)"}
        sx={{ overflowY: "auto", mb: { xs: "6.6rem", md: 0 } }}
      >
        {chatList?.map(renderChatCard)}
        <div ref={lastElementRef} style={{ height: "1px" }} />
        {isLoading && chatList.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
              width: "100%",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      height={"calc(100% - 5rem)"}
      sx={{ overflowY: "auto", mb: { xs: "6.6rem", md: 0 } }}
    >
      {chatList?.map(renderChatCard)}
    </Stack>
  );
};

export default ChatList;
